import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

const AUTH_TOKEN_KEY = 'line_rich_menu_token';
const AUTH_USER_KEY = 'line_rich_menu_user';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
    const storedUser = localStorage.getItem(AUTH_USER_KEY);
    
    if (storedToken && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setToken(storedToken);
        setUser(parsedUser);
        // Set token in API instance
        api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      } catch (e) {
        // Invalid stored data, clear it
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(AUTH_USER_KEY);
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    setLoading(true);
    
    try {
      const response = await api.post('/auth/login', { email, password });
      const { access_token, user: userData } = response.data;
      
      // Store in localStorage (7 days persistence)
      localStorage.setItem(AUTH_TOKEN_KEY, access_token);
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(userData));
      
      // Set in state
      setToken(access_token);
      setUser(userData);
      
      // Set token in API instance
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.detail || 'Login failed';
      setError(message);
      return { success: false, error: message };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    // Clear localStorage
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_USER_KEY);
    
    // Clear state
    setToken(null);
    setUser(null);
    setError(null);
    
    // Remove from API instance
    delete api.defaults.headers.common['Authorization'];
  }, []);

  const refreshUser = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await api.get('/auth/me');
      const userData = response.data;
      setUser(userData);
      localStorage.setItem(AUTH_USER_KEY, JSON.stringify(userData));
    } catch (err) {
      // Token might be expired, logout
      if (err.response?.status === 401) {
        logout();
      }
    }
  }, [token, logout]);

  // isSuperAdmin: global admin (old "admin" role)
  const isSuperAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  // isAdmin: true for superadmins (backward compat — used for sidebar admin-only items)
  const isAdmin = isSuperAdmin;
  const isAuthenticated = !!token && !!user;

  // Helper: get user's role in a specific workspace
  const getWorkspaceRole = (workspaceId) => {
    if (isSuperAdmin) return 'admin';
    const membership = user?.workspaceMemberships?.find(m => m.workspaceId === workspaceId);
    return membership?.role || null;
  };

  const value = {
    user,
    token,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    isSuperAdmin,
    getWorkspaceRole,
    login,
    logout,
    refreshUser,
    setError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
